import React, { useState, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./index.css";

// ============================================================================
// EKDK Panchang Calendar Dynamic Coloring Engine (2026 & 2027 Rule Book)
// Reverse-engineered from "2026 Tamil calendar.indd" & mypanchang/zodiaclist.py
// ============================================================================
//
// Swatches in 2026 Tamil calendar.indd:
// 1. GREEN ("panchang green ", UID 0x0017, CMYK 100/0/100/10):
//    Element is 100% auspicious throughout its [start, end] window on this day.
// 2. RED ("Panchang red", UID 0x256b, CMYK 0/100/100/5):
//    Element is 100% inauspicious throughout its [start, end] window on this day
//    (Tier 1 intrinsic, weekday combination, full-window Tithi+Nakshatra overlap,
//    or initial bad duration extending past 24:00 / window end).
// 3. BLUE ("Panchang blue", UID 0x19e6a, CMYK 100/10/0/0):
//    Element is TIME-SPLIT (partially inauspicious and partially auspicious)
//    during its [start, end] window on this day:
//    - Initial bad duration (bad_tithi_duration / bad_yoga_duration) expires mid-window
//    - Midnight spillover of initial bad duration from previous day expires after 00:00
//    - Partial-window overlap of a bad_tithi_nakshatra combination
// 4. MUHURTA BLUE ("sat tuesday matter blue", UID 0x1c719, CMYK 100/25/0/0):
//    Used for Muhurta checkmarks & timings on Tuesdays (medicine, surgery, debt
//    repayment) and Saturdays (charitable donations).

// --- Tier 1: Immediate (Intrinsic) Negative Rules ---
export const BAD_NAKSHATRA_NUMS = new Set([2, 3, 6, 9, 10, 11, 18, 20, 25]);
export const BAD_NAKSHATRA_NAMES = new Set([
  "bharani", "krittika", "arudra", "ashlesha", "aslesha",
  "magha", "makha", "poorvaphalguni", "poorphaphalguni",
  "jyeshta", "poorvashada", "poorvabhadra",
  "பரணி", "கிருத்திகை", "திருவாதிரை", "ஆயில்யம்",
  "மகம்", "பூரம்", "கேட்டை", "பூராடம்", "பூரட்டாதி",
]);

export const BAD_TITHI_NUMS = new Set([0, 14, 16]);
export const BAD_TITHI_NAMES = new Set([
  "chaturdasi", "chaturdashi", "amavasya",
  "சதுர்த்தசி", "அமாவாசை",
]);

export const BAD_YOGA_NUMS = new Set([17, 27, 28]);
export const BAD_YOGA_NAMES = new Set([
  "vyatipata", "vaidhriti",
  "வ்யாதீபாதம்", "வைதிருதி",
]);

export const BAD_KARANA_NUMS = new Set([7, 8, 9, 10, 11]);
export const BAD_KARANA_NAMES = new Set([
  "vishti", "shakuna", "shakuni", "chatushpada", "naga", "kinstughna", "kimstughna",
  "பத்திரை", "சகுனி", "சதுஷ்பாதம்", "நாகவம்", "கிம்ஸ்துக்கினம்",
]);

// --- Tier 2A: Weekday Combination Rules (Monday = 0 .. Sunday = 6) ---
export const BAD_DAY_NAKSHATRA = {
  0: new Set([14, 16, 21]),         // Monday: Chitra, Vishakha, Uttarashada
  1: new Set([21, 23, 24]),         // Tuesday: Uttarashada, Dhanishta, Satabisha
  2: new Set([1, 19, 23, 27]),      // Wednesday: Ashwini, Moola, Dhanishta, Revati
  3: new Set([4, 5, 12]),           // Thursday: Rohini, Mrigasira, Uttaraphalguni
  4: new Set([4, 8]),               // Friday: Rohini, Pushyami
  5: new Set([12, 13, 14, 27]),     // Saturday: Uttaraphalguni, Hasta, Chitra, Revati
  6: new Set([16, 17]),             // Sunday: Vishakha, Anuradha
};

export const BAD_DAY_TITHI = {
  0: new Set([6, 11, 15]),          // Monday: Shashti, Ekadasi, Poornima
  1: new Set([5, 7, 10, 15]),       // Tuesday: Panchami, Saptami, Dasami, Poornima
  2: new Set([1, 8, 9, 15]),        // Wednesday: Pratipat, Ashtami, Navami, Poornima
  3: new Set([6, 8, 9]),            // Thursday: Shashti, Ashtami, Navami (Poornima 15 is auspicious!)
  4: new Set([8, 9, 10, 15]),       // Friday: Ashtami, Navami, Dasami, Poornima
  5: new Set([6, 7, 9, 11, 15]),    // Saturday: Shashti, Saptami, Navami, Ekadasi, Poornima
  6: new Set([4, 7, 12, 15]),       // Sunday: Chaturthi, Saptami, Dwadasi, Poornima
};

// --- Tier 2B: Initial Bad Durations (in minutes from element start) ---
export const BAD_TITHI_DURATION_MINS = {
  4: 3 * 60 + 11,   // 04. Chaturthi: 3h 11m (191m)
  6: 3 * 60 + 35,   // 06. Shashti:   3h 35m (215m)
  8: 5 * 60 + 35,   // 08. Ashtami:   5h 35m (335m)
  9: 9 * 60 + 59,   // 09. Navami:    9h 59m (599m)
  12: 3 * 60 + 59,  // 12. Dwadasi:   3h 59m (239m)
};

export const BAD_YOGA_DURATION_MINS = {
  1: 1 * 60 + 11,   // 01. Vishkumbha: 1h 11m (71m)
  6: 2 * 60 + 23,   // 06. Atiganda:   2h 23m (143m)
  9: 1 * 60 + 59,   // 09. Shoola:     1h 59m (119m)
  10: 2 * 60 + 23,  // 10. Ganda:      2h 23m (143m)
  13: 3 * 60 + 35,  // 13. Vyaghata:   3h 35m (215m)
  15: 1 * 60 + 11,  // 15. Vajra:      1h 11m (71m)
  19: 11 * 60 + 59, // 19. Parigha:   11h 59m (719m)
};

// --- Tier 2C: Tithi + Nakshatra Combination Rules ---
export const BAD_TITHI_NAKSHATRA_SET = new Set([
  "1:21",  // Pratipat + Uttarashada
  "2:17",  // Dwiteeya + Anuradha
  "3:12",  // Triteeya + Uttaraphalguni
  "3:21",  // Triteeya + Uttarashada
  "3:26",  // Triteeya + Uttarabhadra
  "6:4",   // Shashti + Rohini
  "7:13",  // Saptami + Hasta
  "7:19",  // Saptami + Moola
  "11:4",  // Ekadasi + Rohini
  "13:14", // Trayodasi + Chitra
  "13:15", // Trayodasi + Swati
]);

const MONTHS = [
  { key: "Jan", name: "January", index: 0 },
  { key: "Feb", name: "February", index: 1 },
  { key: "Mar", name: "March", index: 2 },
  { key: "Apr", name: "April", index: 3 },
  { key: "May", name: "May", index: 4 },
  { key: "Jun", name: "June", index: 5 },
  { key: "Jul", name: "July", index: 6 },
  { key: "Aug", name: "August", index: 7 },
  { key: "Sep", name: "September", index: 8 },
  { key: "Oct", name: "October", index: 9 },
  { key: "Nov", name: "November", index: 10 },
  { key: "Dec", name: "December", index: 11 },
];

const WEEKDAYS_HEADER = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const WEEKDAY_INDEX = {
  Monday: 0,
  Tuesday: 1,
  Wednesday: 2,
  Thursday: 3,
  Friday: 4,
  Saturday: 5,
  Sunday: 6,
};

/**
 * Extract numeric prefix (e.g., "06. Arudra ↑" -> 6) and normalized name ("arudra").
 */
export const parsePanchangLabel = (rawLabel) => {
  if (!rawLabel || typeof rawLabel !== "string") return { num: null, name: "" };
  const trimmed = rawLabel.trim();
  const match = trimmed.match(/^(\d+)\.\s*(.+)$/);
  if (match) {
    const num = parseInt(match[1], 10);
    const cleanName = match[2]
      .replace(/[↑↓→]/g, "")
      .replace(/\s+\d{1,2}:\d{2}$/, "")
      .trim()
      .toLowerCase();
    return { num, name: cleanName };
  }
  const cleanName = trimmed
    .replace(/[↑↓→]/g, "")
    .replace(/\s+\d{1,2}:\d{2}$/, "")
    .trim()
    .toLowerCase();
  return { num: null, name: cleanName };
};

const hmToMin = (tStr) => {
  if (!tStr || typeof tStr !== "string" || !tStr.includes(":")) return 0;
  const [h, m] = tStr.trim().split(":").map((v) => parseInt(v, 10) || 0);
  return h * 60 + m;
};

const minToHm = (mins) => {
  const clamped = Math.max(0, Math.min(24 * 60, Math.round(mins)));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const formatDurationHm = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
};

const getLimbIntervals = (dayObj, limbKey) => {
  const arr = dayObj?.[limbKey] || [];
  return arr.map(([label, tStr], i) => {
    const { num, name } = parsePanchangLabel(label);
    const startMin = hmToMin(tStr);
    const endMin = i + 1 < arr.length ? hmToMin(arr[i + 1][1]) : 24 * 60;
    return { num, name, label, tStr: tStr || "00:00", startMin, endMin };
  });
};

/**
 * Tier-1 Immediate Negative Evaluator (used when ruleMode === "tier1").
 */
export const isElementNegative = (category, rawLabel) => {
  const { num, name } = parsePanchangLabel(rawLabel);
  switch (category) {
    case "nakshatra":
      if (num !== null) return BAD_NAKSHATRA_NUMS.has(num);
      return BAD_NAKSHATRA_NAMES.has(name.split(/\s+/)[0]);
    case "tithi":
      if (num !== null) return BAD_TITHI_NUMS.has(num);
      return BAD_TITHI_NAMES.has(name.split(/\s+/).pop());
    case "yoga":
      if (num !== null) return BAD_YOGA_NUMS.has(num);
      return BAD_YOGA_NAMES.has(name.split(/\s+/)[0]);
    case "karana":
      if (num !== null) return BAD_KARANA_NUMS.has(num);
      return BAD_KARANA_NAMES.has(name.split(/\s+/)[0]);
    default:
      return false;
  }
};

/**
 * Full Dynamic 3-Color Evaluator matching the 2026 Tamil Calendar InDesign Rule Book.
 * Returns { status: "GREEN" | "RED" | "BLUE", reason: string, ruleTier: string }
 */
export const evaluatePanchangElement = ({
  dayObj,
  prevDayObj,
  category,
  itemIndex,
  ruleMode = "dynamic",
}) => {
  const intervals = getLimbIntervals(dayObj, category);
  const current = intervals[itemIndex];
  if (!current) {
    return { status: "GREEN", reason: "Auspicious", ruleTier: "Positive" };
  }

  const { num, label, startMin, endMin } = current;
  const wday = WEEKDAY_INDEX[dayObj?.weekday] ?? 0;

  // If user selected static Tier-1-only mode:
  if (ruleMode === "tier1") {
    const isNeg = isElementNegative(category, label);
    return isNeg
      ? { status: "RED", reason: `Tier 1 Immediate Negative (${category})`, ruleTier: "Tier 1" }
      : { status: "GREEN", reason: "Immediate Positive", ruleTier: "Positive" };
  }

  // 1. KARANA: Purely Tier 1 (7..11 are RED, 1..6 are GREEN)
  if (category === "karana") {
    if (isElementNegative("karana", label)) {
      return {
        status: "RED",
        reason: `Inauspicious Karana (#${String(num).padStart(2, "0")}) — Avoid`,
        ruleTier: "Tier 1 (Karana)",
      };
    }
    return {
      status: "GREEN",
      reason: `Auspicious Karana (${minToHm(startMin)}–${minToHm(endMin)})`,
      ruleTier: "Positive",
    };
  }

  // 2. YOGA: Tier 1 (17, 27) + Tier 2B Initial Bad Duration (1, 6, 9, 10, 13, 15, 19) + Midnight Spillover
  if (category === "yoga") {
    if (isElementNegative("yoga", label)) {
      return {
        status: "RED",
        reason: `Inauspicious Yoga (#${String(num).padStart(2, "0")}) — Avoid all day`,
        ruleTier: "Tier 1 (Yoga)",
      };
    }
    if (num !== null && BAD_YOGA_DURATION_MINS[num]) {
      const dur = BAD_YOGA_DURATION_MINS[num];
      let badUntil = 0;
      let isSpillover = false;
      if (itemIndex === 0 && startMin === 0) {
        const prevIntervals = getLimbIntervals(prevDayObj, "yoga");
        const prevLast = prevIntervals[prevIntervals.length - 1];
        if (prevLast && prevLast.num === num && prevLast.startMin > 0) {
          badUntil = prevLast.startMin + dur - 24 * 60;
          isSpillover = true;
        }
      } else {
        badUntil = startMin + dur;
      }

      if (badUntil <= startMin) {
        return {
          status: "GREEN",
          reason: `Initial ${formatDurationHm(dur)} bad duration already expired on previous day — Auspicious today (${minToHm(startMin)}–${minToHm(endMin)})`,
          ruleTier: "Duration Cleared",
        };
      }
      if (badUntil >= endMin) {
        return {
          status: "RED",
          reason: `Initial ${formatDurationHm(dur)} bad duration covers entire remaining window (${minToHm(startMin)}–${minToHm(endMin)})`,
          ruleTier: "Tier 2B (Yoga Duration)",
        };
      }
      return {
        status: "BLUE",
        reason: isSpillover
          ? `Midnight Spillover: Initial ${formatDurationHm(dur)} bad duration from previous day ends at ${minToHm(badUntil)} → Auspicious ${minToHm(badUntil)}–${minToHm(endMin)}`
          : `Time-Split Yoga: Initial ${formatDurationHm(dur)} bad (${minToHm(startMin)}–${minToHm(badUntil)}) → Auspicious after ${minToHm(badUntil)} (${minToHm(badUntil)}–${minToHm(endMin)})`,
        ruleTier: "Tier 2B (Time-Split Blue)",
      };
    }
    return {
      status: "GREEN",
      reason: `Auspicious Yoga (${minToHm(startMin)}–${minToHm(endMin)})`,
      ruleTier: "Positive",
    };
  }

  // 3. NAKSHATRA: Tier 1 + Tier 2A (bad_day_nakshatra) + Tier 2C (bad_tithi_nakshatra)
  if (category === "nakshatra") {
    if (isElementNegative("nakshatra", label)) {
      return {
        status: "RED",
        reason: `Immediate Inauspicious Nakshatra (#${String(num).padStart(2, "0")})`,
        ruleTier: "Tier 1 (Nakshatra)",
      };
    }
    if (num !== null && BAD_DAY_NAKSHATRA[wday]?.has(num)) {
      return {
        status: "RED",
        reason: `Inauspicious Weekday + Nakshatra combination (${dayObj.weekday} + #${String(num).padStart(2, "0")})`,
        ruleTier: "Tier 2A (Weekday + Star)",
      };
    }

    const tIntervals = getLimbIntervals(dayObj, "tithi");
    if (num !== null && tIntervals.length > 0) {
      // Day-2 opening rule: if both tithi[0] and nakshatra[0] are at 00:00 and form a bad_tithi_nakshatra pair
      if (itemIndex === 0 && startMin === 0) {
        const t0 = tIntervals[0];
        const t0AlreadyRed =
          BAD_TITHI_NUMS.has(t0.num) || BAD_DAY_TITHI[wday]?.has(t0.num);
        if (BAD_TITHI_NAKSHATRA_SET.has(`${t0.num}:${num}`) && !t0AlreadyRed) {
          const ovEnd = Math.min(endMin, t0.endMin);
          // Check if subsequent tithis during [ovEnd, endMin] are also intrinsically bad
          const remTithis = tIntervals.filter((ti) => ti.endMin > ovEnd && ti.startMin < endMin);
          const allRemBad =
            remTithis.length > 0 &&
            remTithis.every(
              (ti) => BAD_TITHI_NUMS.has(ti.num) || BAD_DAY_TITHI[wday]?.has(ti.num)
            );
          if (allRemBad) {
            return {
              status: "RED",
              reason: `Inauspicious Tithi+Nakshatra (#${String(t0.num).padStart(2, "0")}+#${String(num).padStart(2, "0")}) followed by inauspicious Tithi`,
              ruleTier: "Tier 2C (Tithi+Star)",
            };
          }
          return {
            status: "BLUE",
            reason: `Time-Split Tithi+Nakshatra (#${String(t0.num).padStart(2, "0")}+#${String(num).padStart(2, "0")}) overlap until ${minToHm(ovEnd)}`,
            ruleTier: "Tier 2C (Time-Split Blue)",
          };
        }
      }

      let badMins = 0;
      let matchedTithi = null;
      const totalMins = Math.max(1, endMin - startMin);
      for (let m = startMin; m < endMin; m++) {
        const activeT =
          tIntervals.find((ti) => ti.startMin <= m && m < ti.endMin) ||
          tIntervals[tIntervals.length - 1];
        const tAlreadyRed =
          BAD_TITHI_NUMS.has(activeT.num) || BAD_DAY_TITHI[wday]?.has(activeT.num);
        if (BAD_TITHI_NAKSHATRA_SET.has(`${activeT.num}:${num}`) && !tAlreadyRed) {
          badMins += 1;
          matchedTithi = activeT;
        }
      }
      if (badMins === totalMins && matchedTithi) {
        return {
          status: "RED",
          reason: `Inauspicious Tithi+Nakshatra (#${String(matchedTithi.num).padStart(2, "0")}+#${String(num).padStart(2, "0")}) throughout entire window (${minToHm(startMin)}–${minToHm(endMin)})`,
          ruleTier: "Tier 2C (Tithi+Star)",
        };
      }
      if (badMins > 0 && matchedTithi) {
        const ovStart = Math.max(startMin, matchedTithi.startMin);
        const ovEnd = Math.min(endMin, matchedTithi.endMin);
        return {
          status: "BLUE",
          reason: `Time-Split Nakshatra: Overlaps Tithi #${String(matchedTithi.num).padStart(2, "0")} (${minToHm(ovStart)}–${minToHm(ovEnd)} bad), auspicious outside overlap`,
          ruleTier: "Tier 2C (Time-Split Blue)",
        };
      }
    }

    return {
      status: "GREEN",
      reason: `Auspicious Nakshatra (${minToHm(startMin)}–${minToHm(endMin)})`,
      ruleTier: "Positive",
    };
  }

  // 4. TITHI: Tier 1 + Tier 2A (bad_day_tithi) + Tier 2B (bad_tithi_duration + spillover) + Tier 2C (bad_tithi_nakshatra)
  if (category === "tithi") {
    if (isElementNegative("tithi", label)) {
      return {
        status: "RED",
        reason: `Immediate Inauspicious Tithi (#${String(num).padStart(2, "0")}) — Avoid all day`,
        ruleTier: "Tier 1 (Tithi)",
      };
    }
    if (num !== null && BAD_DAY_TITHI[wday]?.has(num)) {
      return {
        status: "RED",
        reason: `Inauspicious Weekday + Tithi combination (${dayObj.weekday} + #${String(num).padStart(2, "0")})`,
        ruleTier: "Tier 2A (Weekday + Tithi)",
      };
    }

    let durBadUntil = -1;
    let isSpillover = false;
    const dur = num !== null ? BAD_TITHI_DURATION_MINS[num] || 0 : 0;
    if (dur > 0) {
      if (itemIndex === 0 && startMin === 0) {
        const prevIntervals = getLimbIntervals(prevDayObj, "tithi");
        const prevLast = prevIntervals[prevIntervals.length - 1];
        if (prevLast && prevLast.num === num && prevLast.startMin > 0) {
          durBadUntil = prevLast.startMin + dur - 24 * 60;
          isSpillover = true;
        }
      } else {
        durBadUntil = startMin + dur;
      }
    }

    const nIntervals = getLimbIntervals(dayObj, "nakshatra");
    if (itemIndex === 0 && startMin === 0 && durBadUntil <= 0 && nIntervals.length > 0) {
      const n0 = nIntervals[0];
      const n0AlreadyRed =
        BAD_NAKSHATRA_NUMS.has(n0.num) || BAD_DAY_NAKSHATRA[wday]?.has(n0.num);
      if (BAD_TITHI_NAKSHATRA_SET.has(`${num}:${n0.num}`) && !n0AlreadyRed) {
        const ovEnd = Math.min(endMin, n0.endMin);
        if (endMin <= n0.endMin && itemIndex + 1 < intervals.length) {
          const nextT = intervals[itemIndex + 1];
          if (BAD_TITHI_NUMS.has(nextT.num)) {
            return {
              status: "RED",
              reason: `Inauspicious Tithi+Nakshatra (#${String(num).padStart(2, "0")}+#${String(n0.num).padStart(2, "0")}) throughout entire window (${minToHm(startMin)}–${minToHm(endMin)})`,
              ruleTier: "Tier 2C (Tithi+Star)",
            };
          }
        }
        return {
          status: "BLUE",
          reason: `Time-Split Tithi+Nakshatra (#${String(num).padStart(2, "0")}+#${String(n0.num).padStart(2, "0")}) overlap until ${minToHm(ovEnd)}`,
          ruleTier: "Tier 2C (Time-Split Blue)",
        };
      }
    }

    let badMins = 0;
    let matchedNak = null;
    const totalMins = Math.max(1, endMin - startMin);
    for (let m = startMin; m < endMin; m++) {
      let isBad = false;
      if (m < durBadUntil) {
        isBad = true;
      } else if (nIntervals.length > 0) {
        const activeN =
          nIntervals.find((ni) => ni.startMin <= m && m < ni.endMin) ||
          nIntervals[nIntervals.length - 1];
        const nAlreadyRed =
          BAD_NAKSHATRA_NUMS.has(activeN.num) || BAD_DAY_NAKSHATRA[wday]?.has(activeN.num);
        if (BAD_TITHI_NAKSHATRA_SET.has(`${num}:${activeN.num}`) && !nAlreadyRed) {
          isBad = true;
          matchedNak = activeN;
        }
      }
      if (isBad) badMins += 1;
    }

    if (badMins === 0) {
      if (dur > 0) {
        return {
          status: "GREEN",
          reason: `Initial ${formatDurationHm(dur)} bad duration already expired on previous day — Auspicious today (${minToHm(startMin)}–${minToHm(endMin)})`,
          ruleTier: "Duration Cleared",
        };
      }
      return {
        status: "GREEN",
        reason: `Auspicious Tithi (${minToHm(startMin)}–${minToHm(endMin)})`,
        ruleTier: "Positive",
      };
    }

    if (badMins === totalMins) {
      if (durBadUntil >= endMin) {
        return {
          status: "RED",
          reason: `Initial ${formatDurationHm(dur)} bad duration covers entire remaining window (${minToHm(startMin)}–${minToHm(endMin)})`,
          ruleTier: "Tier 2B (Tithi Duration)",
        };
      }
      return {
        status: "RED",
        reason: matchedNak
          ? `Inauspicious Tithi+Nakshatra (#${String(num).padStart(2, "0")}+#${String(matchedNak.num).padStart(2, "0")}) throughout entire window (${minToHm(startMin)}–${minToHm(endMin)})`
          : `Inauspicious throughout window (${minToHm(startMin)}–${minToHm(endMin)})`,
        ruleTier: "Tier 2C (Tithi+Star)",
      };
    }

    if (durBadUntil > startMin && durBadUntil < endMin) {
      return {
        status: "BLUE",
        reason: isSpillover
          ? `Midnight Spillover: Initial ${formatDurationHm(dur)} bad duration from previous day ends at ${minToHm(durBadUntil)} → Auspicious ${minToHm(durBadUntil)}–${minToHm(endMin)}`
          : `Time-Split Tithi: Initial ${formatDurationHm(dur)} bad (${minToHm(startMin)}–${minToHm(durBadUntil)}) → Auspicious after ${minToHm(durBadUntil)} (${minToHm(durBadUntil)}–${minToHm(endMin)})`,
        ruleTier: "Tier 2B (Time-Split Blue)",
      };
    }

    return {
      status: "BLUE",
      reason: matchedNak
        ? `Time-Split Tithi: Overlaps Nakshatra #${String(matchedNak.num).padStart(2, "0")} (${minToHm(Math.max(startMin, matchedNak.startMin))}–${minToHm(Math.min(endMin, matchedNak.endMin))} bad), auspicious outside overlap`
        : `Partially inauspicious / partially auspicious (${minToHm(startMin)}–${minToHm(endMin)})`,
      ruleTier: "Tier 2C (Time-Split Blue)",
    };
  }

  return { status: "GREEN", reason: "Auspicious", ruleTier: "Positive" };
};

const displayTime = (t, showMidnight) => {
  const isEmpty = t === undefined || t === null || t === "" || t === "00:00";
  if (isEmpty && !showMidnight) return "";
  return t || "00:00";
};

const formatMuhurta = (muhurta, showMidnight) => {
  if (!muhurta || typeof muhurta !== "object") return [];
  const entries = Object.entries(muhurta).reduce((acc, [key, value]) => {
    const idx = key.match(/\d+/)?.[0];
    if (!idx) return acc;
    if (!acc[idx]) acc[idx] = {};
    acc[idx][key.includes("start") ? "start" : "end"] = value;
    return acc;
  }, {});
  return Object.entries(entries)
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([, { start, end }]) => `${displayTime(start, showMidnight) || "00:00"} - ${end || "24:00"}`);
};

const isRestrictedMuhurtaWeekday = (weekday) =>
  weekday === "Tuesday" || weekday === "Saturday";

const getMuhurtaWeekdayNote = (weekday) => {
  if (weekday === "Tuesday") {
    return "Tuesday Muhurta (Blue ✓): Suitable only for medicine, surgery, and debt repayment (மருந்து, அறுவை சிகிச்சை, கடன் அடைப்பது).";
  }
  if (weekday === "Saturday") {
    return "Saturday Muhurta (Blue ✓): Suitable only for charitable donations of food, clothing, medicine, and money (தானம் செய்வது).";
  }
  return "Auspicious Subha Muhurta (Green ✓): Suitable for general auspicious ceremonies.";
};

const CalendarFormat = ({ panchangData, showMidnight = true }) => {
  const [selectedMonthIdx, setSelectedMonthIdx] = useState(0);
  const [filterCategory, setFilterCategory] = useState("all"); // all | nakshatra | tithi | yoga | karana
  const [ruleMode, setRuleMode] = useState("dynamic"); // dynamic | tier1
  const [showRuleBook, setShowRuleBook] = useState(false);
  const [selectedDayEntry, setSelectedDayEntry] = useState(null);

  // Attach globalIndex and prevDayObj to every entry so midnight spillover works across month boundaries
  const enrichedData = useMemo(() => {
    const list = panchangData || [];
    return list.map((entry, idx) => {
      const [mKey, dStr] = (entry?.date || "Jan 01").trim().split(/\s+/);
      return {
        ...entry,
        monthKey: mKey,
        dayNum: parseInt(dStr, 10) || 1,
        globalIndex: idx,
        prevDayObj: idx > 0 ? list[idx - 1] : null,
      };
    });
  }, [panchangData]);

  // Group enriched entries by month ("Jan", "Feb", ...)
  const entriesByMonth = useMemo(() => {
    const map = {};
    MONTHS.forEach((m) => {
      map[m.key] = [];
    });
    enrichedData.forEach((entry) => {
      if (map[entry.monthKey]) {
        map[entry.monthKey].push(entry);
      }
    });
    return map;
  }, [enrichedData]);

  const currentMonth = MONTHS[selectedMonthIdx] || MONTHS[0];
  const monthDays = useMemo(
    () => entriesByMonth[currentMonth.key] || [],
    [entriesByMonth, currentMonth]
  );

  // Compute leading empty cells for Monday-first calendar grid
  const leadingEmptyCount = useMemo(() => {
    if (monthDays.length === 0) return 0;
    const firstDay = monthDays[0];
    const wdIndex = WEEKDAY_INDEX[firstDay.weekday];
    return typeof wdIndex === "number" ? wdIndex : 0;
  }, [monthDays]);

  const trailingEmptyCount = useMemo(() => {
    const totalCells = leadingEmptyCount + monthDays.length;
    const remainder = totalCells % 7;
    return remainder === 0 ? 0 : 7 - remainder;
  }, [leadingEmptyCount, monthDays]);

  // Month statistics
  const monthStats = useMemo(() => {
    let greenCount = 0;
    let blueCount = 0;
    let redCount = 0;
    let greenMuhurtaDays = 0;
    let blueMuhurtaDays = 0;

    monthDays.forEach((day) => {
      ["nakshatra", "tithi", "yoga", "karana"].forEach((limb) => {
        (day[limb] || []).forEach((_, idx) => {
          const { status } = evaluatePanchangElement({
            dayObj: day,
            prevDayObj: day.prevDayObj,
            category: limb,
            itemIndex: idx,
            ruleMode,
          });
          if (status === "GREEN") greenCount += 1;
          else if (status === "BLUE") blueCount += 1;
          else redCount += 1;
        });
      });
      if (day.muhurta && Object.keys(day.muhurta).length > 0) {
        if (isRestrictedMuhurtaWeekday(day.weekday)) {
          blueMuhurtaDays += 1;
        } else {
          greenMuhurtaDays += 1;
        }
      }
    });

    return { greenCount, blueCount, redCount, greenMuhurtaDays, blueMuhurtaDays };
  }, [monthDays, ruleMode]);

  const getPillClass = (status) => {
    if (status === "RED") return "cal-pill-negative";
    if (status === "BLUE") return "cal-pill-partial";
    return "cal-pill-positive";
  };

  const renderCategoryRows = (day, categoryKey, categoryLabel, shortBadge) => {
    if (filterCategory !== "all" && filterCategory !== categoryKey) return null;
    const items = day[categoryKey] || [];
    if (items.length === 0) return null;

    return (
      <div className="cal-category-group">
        <div className="cal-category-header">{categoryLabel}</div>
        {items.map(([label, time], idx) => {
          const evalResult = evaluatePanchangElement({
            dayObj: day,
            prevDayObj: day.prevDayObj,
            category: categoryKey,
            itemIndex: idx,
            ruleMode,
          });
          const formattedTime = displayTime(time, showMidnight);
          return (
            <div
              key={`${categoryKey}-${idx}`}
              className={`cal-panchang-pill ${getPillClass(evalResult.status)}`}
              title={`${categoryLabel}: ${label} ${
                formattedTime ? `(${formattedTime})` : ""
              }\nStatus: ${evalResult.status} (${evalResult.ruleTier})\n${evalResult.reason}`}
            >
              <span className="cal-pill-badge">{shortBadge}</span>
              <span className="cal-pill-name">{label}</span>
              {formattedTime && <span className="cal-pill-time">{formattedTime}</span>}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="calendar-view-wrapper">
      {/* Legend & Rule Book Bar */}
      <div className="cal-legend-card mb-3">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div className="d-flex flex-wrap align-items-center gap-3">
            <span className="fw-bold">Panchang Color Key:</span>
            <span className="cal-legend-item" title="100% Auspicious throughout its window on this day">
              <span className="cal-dot cal-dot-positive" /> Green (Auspicious)
            </span>
            {ruleMode === "dynamic" && (
              <span
                className="cal-legend-item"
                title="Time-Split: Partially inauspicious & partially auspicious on this day (Initial Bad Duration or Tithi+Nakshatra partial overlap)"
              >
                <span className="cal-dot cal-dot-partial" /> Blue (Time-Split / Partial)
              </span>
            )}
            <span className="cal-legend-item" title="100% Inauspicious throughout its window on this day">
              <span className="cal-dot cal-dot-negative" /> Red (Avoid)
            </span>
            <span
              className="cal-legend-item"
              title="Sun/Mon/Wed/Thu/Fri Muhurta: General auspiciousness (Green ✓) · Tue/Sat Muhurta: Medicine/Surgery/Debt or Charity only (Blue ✓)"
            >
              <span className="cal-muhurta-check cal-muhurta-green">✓</span>/
              <span className="cal-muhurta-check cal-muhurta-blue">✓</span> Muhurta (General / Tue·Sat)
            </span>
          </div>

          <div className="d-flex flex-wrap align-items-center gap-2">
            <div className="d-flex align-items-center gap-1">
              <label htmlFor="calRuleModeSelect" className="small fw-bold me-1">
                Rule Engine:
              </label>
              <select
                id="calRuleModeSelect"
                className="form-select form-select-sm w-auto"
                value={ruleMode}
                onChange={(e) => setRuleMode(e.target.value)}
              >
                <option value="dynamic">Dynamic 3-Color (2026/2027 Calendar Rulebook)</option>
                <option value="tier1">Tier 1 Only (Static Immediate Red/Green)</option>
              </select>
            </div>
            <button
              type="button"
              className="btn btn-sm theme-toggle-btn"
              onClick={() => setShowRuleBook((prev) => !prev)}
            >
              {showRuleBook ? "▲ Hide Rule Book" : "📖 Rule Book"}
            </button>
          </div>
        </div>

        {/* Collapsible Complete Rule Book Reference */}
        {showRuleBook && (
          <div className="cal-rulebook-drawer mt-3 pt-3 border-top border-secondary border-opacity-25">
            <h5 className="mb-2">EKDK Tamil Calendar Dynamic Coloring Rule Book (2026 & 2027)</h5>
            <p className="small text-muted mb-3">
              Derived directly from the <strong>2026 Tamil Calendar InDesign master</strong> (
              <code>panchang green</code>, <code>Panchang red</code>, <code>Panchang blue</code>,{" "}
              <code>sat tuesday matter blue</code>) and synchronized with <code>mypanchang</code>&apos;s
              1-minute Muhurta engine. Because a single Panchang line on a calendar day can be bad for
              part of its duration and auspicious for the rest, the calendar uses <strong>Blue</strong>{" "}
              to represent time-split elements.
            </p>
            <div className="row g-2 small">
              <div className="col-12 col-md-6 col-xl-3">
                <div className="cal-detail-box">
                  <div className="cal-detail-box-title text-danger">
                    1. Tier 1 — Immediate Red (Always Bad)
                  </div>
                  <ul className="mb-0 ps-3">
                    <li>
                      <strong>Nakshatra (9):</strong> 02. Bharani, 03. Krittika, 06. Arudra, 09. Ashlesha,
                      10. Magha, 11. Poorvaphalguni, 18. Jyeshta, 20. Poorvashada, 25. Poorvabhadra
                    </li>
                    <li>
                      <strong>Tithi (2):</strong> 14. Chaturdasi, 00. Amavasya
                    </li>
                    <li>
                      <strong>Yoga (2):</strong> 17. Vyatipata, 27. Vaidhriti
                    </li>
                    <li>
                      <strong>Karana (5):</strong> 07. Vishti (Bhadra), 08. Shakuna, 09. Chatushpada,
                      10. Naga, 11. Kinstughna
                    </li>
                  </ul>
                </div>
              </div>

              <div className="col-12 col-md-6 col-xl-3">
                <div className="cal-detail-box">
                  <div className="cal-detail-box-title text-danger">
                    2. Tier 2A — Weekday Combinations (Red)
                  </div>
                  <ul className="mb-0 ps-3">
                    <li>
                      <strong>Mon:</strong> Nak 14, 16, 21 · Tithi 6, 11, 15
                    </li>
                    <li>
                      <strong>Tue:</strong> Nak 21, 23, 24 · Tithi 5, 7, 10, 15
                    </li>
                    <li>
                      <strong>Wed:</strong> Nak 1, 19, 23, 27 · Tithi 1, 8, 9, 15
                    </li>
                    <li>
                      <strong>Thu:</strong> Nak 4, 5, 12 · Tithi 6, 8, 9 <em>(Poornima 15 is Green!)</em>
                    </li>
                    <li>
                      <strong>Fri:</strong> Nak 4, 8 · Tithi 8, 9, 10, 15
                    </li>
                    <li>
                      <strong>Sat:</strong> Nak 12, 13, 14, 27 · Tithi 6, 7, 9, 11, 15
                    </li>
                    <li>
                      <strong>Sun:</strong> Nak 16, 17 · Tithi 4, 7, 12, 15
                    </li>
                  </ul>
                </div>
              </div>

              <div className="col-12 col-md-6 col-xl-3">
                <div className="cal-detail-box">
                  <div className="cal-detail-box-title text-info">
                    3. Tier 2B — Initial Bad Durations (Blue / Red / Green)
                  </div>
                  <p className="mb-1">
                    Inauspicious only for an initial duration after starting. Colored{" "}
                    <strong>Blue</strong> if it expires before 24:00 (or spills over from yesterday and
                    expires today), <strong>Red</strong> if it extends past 24:00, and{" "}
                    <strong>Green</strong> on Day 2 if it already expired yesterday:
                  </p>
                  <ul className="mb-0 ps-3">
                    <li>
                      <strong>Tithi:</strong> #04 (3h 11m), #06 (3h 35m), #08 (5h 35m), #09 (9h 59m),
                      #12 (3h 59m)
                    </li>
                    <li>
                      <strong>Yoga:</strong> #01 (1h 11m), #06 (2h 23m), #09 (1h 59m), #10 (2h 23m),
                      #13 (3h 35m), #15 (1h 11m), #19 (11h 59m)
                    </li>
                  </ul>
                </div>
              </div>

              <div className="col-12 col-md-6 col-xl-3">
                <div className="cal-detail-box">
                  <div className="cal-detail-box-title text-info">
                    4. Tier 2C — Tithi+Star Overlap & Tue/Sat Muhurta
                  </div>
                  <ul className="mb-0 ps-3">
                    <li>
                      <strong>Bad (Tithi, Star) Pairs:</strong> (1,21), (2,17), (3,12), (3,21), (3,26),
                      (6,4), (7,13), (7,19), (11,4), (13,14), (13,15).
                    </li>
                    <li>
                      If the overlap covers 100% of an element&apos;s window today → <strong>Red</strong>;
                      if partial → <strong>Blue</strong>.
                    </li>
                    <li>
                      <strong>Tue / Sat Muhurta (Blue ✓):</strong> Tuesday Muhurtas are for medicine,
                      surgery &amp; debt repayment; Saturday Muhurtas are for charitable donations.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Month Navigation & Filter Controls */}
      <div className="cal-controls-bar mb-3">
        <div className="cal-month-tabs">
          {MONTHS.map((m, idx) => {
            const count = entriesByMonth[m.key]?.length || 0;
            return (
              <button
                key={m.key}
                type="button"
                disabled={count === 0}
                className={`cal-month-btn ${selectedMonthIdx === idx ? "active" : ""}`}
                onClick={() => {
                  setSelectedMonthIdx(idx);
                  setSelectedDayEntry(null);
                }}
              >
                {m.key}
              </button>
            );
          })}
        </div>

        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-2 pt-2 border-top border-secondary border-opacity-25">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-sm theme-toggle-btn"
              onClick={() => setSelectedMonthIdx((prev) => (prev > 0 ? prev - 1 : 11))}
            >
              ◀ Prev
            </button>
            <h3 className="cal-month-title m-0 px-2">{currentMonth.name}</h3>
            <button
              type="button"
              className="btn btn-sm theme-toggle-btn"
              onClick={() => setSelectedMonthIdx((prev) => (prev < 11 ? prev + 1 : 0))}
            >
              Next ▶
            </button>
          </div>

          <div className="d-flex flex-wrap align-items-center gap-3">
            <div className="cal-stats-badges d-none d-md-flex align-items-center gap-2 small">
              <span className="cal-stat-chip cal-stat-pos">
                Green: {monthStats.greenCount}
              </span>
              {ruleMode === "dynamic" && (
                <span className="cal-stat-chip cal-stat-partial">
                  Blue (Split): {monthStats.blueCount}
                </span>
              )}
              <span className="cal-stat-chip cal-stat-neg">
                Red: {monthStats.redCount}
              </span>
              <span className="cal-stat-chip cal-stat-muhurta">
                Muhurta Days: {monthStats.greenMuhurtaDays} ✓ / {monthStats.blueMuhurtaDays} (Tue·Sat)
              </span>
            </div>

            <div className="d-flex align-items-center gap-1">
              <label htmlFor="calFilterSelect" className="small fw-bold me-1">
                Show:
              </label>
              <select
                id="calFilterSelect"
                className="form-select form-select-sm w-auto"
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <option value="all">All 4 Limbs (N / T / Y / K)</option>
                <option value="nakshatra">Nakshatra Only</option>
                <option value="tithi">Tithi Only</option>
                <option value="yoga">Yoga Only</option>
                <option value="karana">Karana Only</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Selected Day Detailed Drawer / Modal */}
      {selectedDayEntry && (
        <div className="cal-day-detail-card mb-3">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div>
              <h4 className="m-0">
                {selectedDayEntry.date} — {selectedDayEntry.weekday}
              </h4>
              <div className="small text-muted mt-1">
                Rahukalam: <strong>{selectedDayEntry.rahukalam}</strong> · Yamagandam:{" "}
                <strong>{selectedDayEntry.yamagandam}</strong>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-sm theme-toggle-btn"
              onClick={() => setSelectedDayEntry(null)}
            >
              ✕ Close
            </button>
          </div>

          <div className="row g-2 mt-1">
            {[
              ["nakshatra", "Nakshatra (Star)", "N"],
              ["tithi", "Tithi (Lunar Day)", "T"],
              ["yoga", "Yoga", "Y"],
              ["karana", "Karana", "K"],
            ].map(([key, title, badge]) => (
              <div key={key} className="col-12 col-sm-6 col-lg-3">
                <div className="cal-detail-box">
                  <div className="cal-detail-box-title">{title}</div>
                  {(selectedDayEntry[key] || []).map(([lbl, tm], i) => {
                    const evalResult = evaluatePanchangElement({
                      dayObj: selectedDayEntry,
                      prevDayObj: selectedDayEntry.prevDayObj,
                      category: key,
                      itemIndex: i,
                      ruleMode,
                    });
                    return (
                      <div key={i} className="mb-2">
                        <div
                          className={`cal-panchang-pill ${getPillClass(evalResult.status)}`}
                        >
                          <span className="cal-pill-badge">{badge}</span>
                          <span className="cal-pill-name">{lbl}</span>
                          <span className="cal-pill-time">{displayTime(tm, showMidnight)}</span>
                        </div>
                        <div className="cal-pill-reason-subtext mt-1">
                          <strong>{evalResult.ruleTier}:</strong> {evalResult.reason}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="row g-2 mt-2">
            <div className="col-12 col-md-6">
              <div className="cal-detail-box">
                <div className="cal-detail-box-title d-flex align-items-center justify-content-between">
                  <span>Favorable Timings (Final Muhurta)</span>
                  {formatMuhurta(selectedDayEntry.muhurta, showMidnight).length > 0 && (
                    <span
                      className={`cal-muhurta-check ${
                        isRestrictedMuhurtaWeekday(selectedDayEntry.weekday)
                          ? "cal-muhurta-blue"
                          : "cal-muhurta-green"
                      }`}
                    >
                      ✓ {isRestrictedMuhurtaWeekday(selectedDayEntry.weekday) ? "Tue/Sat Blue" : "Subha Muhurta"}
                    </span>
                  )}
                </div>
                {formatMuhurta(selectedDayEntry.muhurta, showMidnight).length > 0 ? (
                  <>
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      {formatMuhurta(selectedDayEntry.muhurta, showMidnight).map((m, i) => (
                        <span
                          key={i}
                          className={`cal-stat-chip ${
                            isRestrictedMuhurtaWeekday(selectedDayEntry.weekday)
                              ? "cal-stat-partial"
                              : "cal-stat-pos"
                          }`}
                        >
                          {m}
                        </span>
                      ))}
                    </div>
                    <div className="small text-muted">
                      {getMuhurtaWeekdayNote(selectedDayEntry.weekday)}
                    </div>
                  </>
                ) : (
                  <span className="text-muted fst-italic small">No favourable timings</span>
                )}
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div className="cal-detail-box">
                <div className="cal-detail-box-title">Rashi & Avoidance Summary</div>
                <div className="small">
                  <strong>Rashi:</strong>{" "}
                  {(selectedDayEntry.rashi || [])
                    .map(([r, t]) => `${r} (${displayTime(t, showMidnight) || "00:00"})`)
                    .join(", ") || "—"}
                </div>
                {selectedDayEntry.activities && selectedDayEntry.activities.length > 0 && (
                  <div className="small mt-1">
                    <strong>Recommended Activities:</strong>{" "}
                    {selectedDayEntry.activities.filter(Boolean).join(" ")}
                  </div>
                )}
                {selectedDayEntry.inauspicious && (
                  <div className="small text-danger mt-1">
                    <strong>Engine Exclusion Log:</strong> {selectedDayEntry.inauspicious}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Monthly Calendar 7-Column Grid */}
      <div className="cal-grid-container">
        <div className="cal-weekday-header">
          {WEEKDAYS_HEADER.map((wd) => (
            <div key={wd} className="cal-weekday-cell">
              {wd}
            </div>
          ))}
        </div>

        <div className="cal-days-grid">
          {Array.from({ length: leadingEmptyCount }).map((_, idx) => (
            <div key={`empty-start-${idx}`} className="cal-day-gate cal-day-empty" />
          ))}

          {monthDays.map((day) => {
            const statuses = [];
            ["nakshatra", "tithi", "yoga", "karana"].forEach((limb) => {
              (day[limb] || []).forEach((_, idx) => {
                const { status } = evaluatePanchangElement({
                  dayObj: day,
                  prevDayObj: day.prevDayObj,
                  category: limb,
                  itemIndex: idx,
                  ruleMode,
                });
                statuses.push(status);
              });
            });

            const posCount = statuses.filter((s) => s === "GREEN").length;
            const blueCount = statuses.filter((s) => s === "BLUE").length;
            const negCount = statuses.filter((s) => s === "RED").length;
            const hasMuhurta = day.muhurta && Object.keys(day.muhurta).length > 0;
            const isBlueMuhurta = hasMuhurta && isRestrictedMuhurtaWeekday(day.weekday);
            const isSelected = selectedDayEntry?.date === day.date;

            let gatePolarityClass = "cal-gate-mixed";
            if (negCount === 0 && blueCount === 0 && posCount > 0) {
              gatePolarityClass = "cal-gate-all-positive";
            } else if (posCount === 0 && blueCount === 0 && negCount > 0) {
              gatePolarityClass = "cal-gate-all-negative";
            }

            const muhurtaLines = formatMuhurta(day.muhurta, showMidnight);

            return (
              <div
                key={day.date}
                className={`cal-day-gate ${gatePolarityClass} ${isSelected ? "cal-gate-selected" : ""}`}
                onClick={() => setSelectedDayEntry(isSelected ? null : day)}
              >
                <div className="cal-gate-top">
                  <div className="d-flex align-items-baseline gap-1">
                    <span className="cal-day-number">{String(day.dayNum).padStart(2, "0")}</span>
                    <span className="cal-day-wk-short d-md-none">{day.weekday.slice(0, 3)}</span>
                  </div>
                  <div className="d-flex align-items-center gap-1">
                    {hasMuhurta && (
                      <span
                        className={`cal-muhurta-check ${
                          isBlueMuhurta ? "cal-muhurta-blue" : "cal-muhurta-green"
                        }`}
                        title={`${getMuhurtaWeekdayNote(day.weekday)}\n${muhurtaLines.join("\n")}`}
                      >
                        ✓
                      </span>
                    )}
                    <span
                      className="cal-polarity-summary"
                      title={`${posCount} Green (Good), ${blueCount} Blue (Time-Split), ${negCount} Red (Avoid)`}
                    >
                      <span className="text-pos">{posCount}G</span>
                      {ruleMode === "dynamic" && blueCount > 0 && (
                        <>
                          /<span className="text-partial">{blueCount}B</span>
                        </>
                      )}
                      /<span className="text-neg">{negCount}R</span>
                    </span>
                  </div>
                </div>

                <div className="cal-gate-body">
                  {renderCategoryRows(day, "nakshatra", "Nakshatra", "N")}
                  {renderCategoryRows(day, "tithi", "Tithi", "T")}
                  {renderCategoryRows(day, "yoga", "Yoga", "Y")}
                  {renderCategoryRows(day, "karana", "Karana", "K")}
                </div>

                {hasMuhurta && (
                  <div
                    className={`cal-gate-muhurta-bar mt-1 ${
                      isBlueMuhurta ? "cal-muhurta-bar-blue" : "cal-muhurta-bar-green"
                    }`}
                    title={getMuhurtaWeekdayNote(day.weekday)}
                  >
                    <span className="fw-bold me-1">✓</span>
                    <span>{muhurtaLines.join(", ")}</span>
                  </div>
                )}
              </div>
            );
          })}

          {Array.from({ length: trailingEmptyCount }).map((_, idx) => (
            <div key={`empty-end-${idx}`} className="cal-day-gate cal-day-empty" />
          ))}
        </div>
      </div>
    </div>
  );
};

export default CalendarFormat;
